import { METRICS_EVENTS } from "@cede/types";
import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useHubUser } from "@/hooks/user/useHubUser";
import { cancelAutomaticStreak, cancelAutomaticStreakV2 } from "@/utils/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

export interface UseCancelStreakOptions {
  version?: 1 | 2;
  onSuccess?: (streakId: string) => void;
  onError?: (error: string) => void;
}

export const useCancelStreak = (options?: UseCancelStreakOptions) => {
  const { auth } = useHubUser();
  const queryClient = useQueryClient();
  const { trackEvent } = useMetricsEvents();
  const { enqueueSnackbar } = useSnackbar();

  const version = options?.version ?? 1;

  const mutation = useMutation({
    mutationFn: async (streakId: string) => {
      const result =
        version === 2 ? await cancelAutomaticStreakV2(auth, streakId) : await cancelAutomaticStreak(auth, streakId);

      if (isServerApiError(result) || isServerApiRfcError(result)) {
        const errorMessage = isServerApiRfcError(result) ? result.detail : result.message;
        throw new Error(errorMessage);
      }

      return result;
    },
    onSuccess: (_data, streakId) => {
      // Track streak cancellation
      trackEvent(METRICS_EVENTS.AUTOMATIC_STREAK_CANCELLED, {
        streak_id: streakId,
        ...(version === 2 && { version: "V2" }),
      });

      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAKS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAK, streakId] });

      enqueueSnackbar("Autopilot cancelled successfully.", {
        variant: "info",
      });

      options?.onSuccess?.(streakId);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to cancel autopilot: ${error.message}`, {
        variant: "error",
      });

      options?.onError?.(error.message);
    },
  });

  return {
    cancelStreak: mutation.mutateAsync,
    isCancelling: mutation.isPending,
    error: mutation.error?.message,
  };
};
